import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Input';
import { Logo } from '@/components/brand/Logo';
import { useToast } from '@/components/ui/Toast';

const schema = z
  .object({
    password: z.string().min(8, 'Kata sandi minimal 8 karakter.').max(128),
    confirm: z.string().min(1, 'Ulangi kata sandi baru.'),
  })
  .refine((d) => d.password === d.confirm, {
    message: 'Kedua kata sandi belum sama.',
    path: ['confirm'],
  });

/**
 * Menyetel kata sandi baru memakai token dari tautan email.
 *
 * Konfirmasi ulang tidak dikirim ke server - ia murni penjaga salah ketik di
 * sisi klien. Menyetel kata sandi yang salah ketik pada halaman ini berarti
 * terkunci dari akun sendiri, karena seluruh sesi ikut dicabut setelahnya.
 */
export const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const token = searchParams.get('token') ?? '';
  const form = useForm({ resolver: zodResolver(schema), mode: 'onBlur' });

  const onSubmit = async (values) => {
    try {
      await api.post('/auth/reset-password', { token, password: values.password });
      toast.success('Kata sandi berhasil diubah', {
        description: 'Silakan masuk dengan kata sandi baru Anda.',
      });
      navigate('/masuk', { replace: true });
    } catch (err) {
      form.setError('password', {
        message:
          err instanceof ApiError ? err.message : 'Gagal menghubungi server. Coba lagi.',
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[var(--surface-canvas)] px-4 py-10">
      <Link to="/" className="mb-7">
        <Logo />
      </Link>

      <div className="w-full max-w-md rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-raised)] p-6 shadow-sm sm:p-7">
        {!token ? (
          <div className="text-center">
            <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-danger-50 text-danger-600 dark:bg-danger-500/15">
              <ShieldAlert className="size-6" aria-hidden="true" />
            </div>
            <h1 className="text-lg font-bold text-[var(--text-primary)]">Tautan tidak lengkap</h1>
            <p className="mt-2 text-sm text-[var(--text-secondary)]">
              Halaman ini perlu token dari tautan yang dikirim ke email Anda. Minta tautan baru,
              lalu buka langsung dari email.
            </p>
            <Link
              to="/lupa-sandi"
              className="mt-6 flex w-full items-center justify-center rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
            >
              Minta tautan baru
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-lg font-bold text-[var(--text-primary)]">Buat kata sandi baru</h1>
            <p className="mt-1.5 mb-6 text-sm text-[var(--text-secondary)]">
              Setelah diubah, semua perangkat yang sedang masuk akan dikeluarkan.
            </p>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
              <Field
                label="Kata Sandi Baru"
                required
                error={form.formState.errors.password?.message}
              >
                {(props) => (
                  <div className="relative">
                    <Input
                      {...props}
                      {...form.register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Minimal 8 karakter"
                      autoComplete="new-password"
                      autoFocus
                      error={form.formState.errors.password}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? 'Sembunyikan kata sandi' : 'Perlihatkan kata sandi'}
                      className="absolute top-1/2 right-3 -translate-y-1/2 text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
                    >
                      {showPassword ? (
                        <EyeOff className="size-4" aria-hidden="true" />
                      ) : (
                        <Eye className="size-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                )}
              </Field>

              <Field
                label="Ulangi Kata Sandi"
                required
                error={form.formState.errors.confirm?.message}
              >
                {(props) => (
                  <Input
                    {...props}
                    {...form.register('confirm')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ketik ulang kata sandi baru"
                    autoComplete="new-password"
                    error={form.formState.errors.confirm}
                  />
                )}
              </Field>

              <Button type="submit" className="w-full" loading={form.formState.isSubmitting}>
                Simpan kata sandi baru
              </Button>
            </form>

            <Link
              to="/masuk"
              className="mt-6 flex items-center justify-center gap-1.5 text-sm font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
            >
              <ArrowLeft className="size-4" aria-hidden="true" />
              Kembali ke halaman masuk
            </Link>
          </>
        )}
      </div>
    </div>
  );
};
